import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Instagram, Linkedin, Twitter, ArrowUpRight } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import { MAP_LINK } from "@/data/content";

const CONTACT_METHODS = [
  {
    Icon: Mail,
    label: "Email",
    value: "contact@incer.in",
    href: "mailto:contact@incer.in",
    note: "Best for partnership & campaign inquiries",
    testId: "contact-email",
  },
  {
    Icon: Phone,
    label: "Phone",
    value: "+91 90872 55512",
    href: "tel:+919087255512",
    note: "Mon – Sat · 10am to 7pm IST",
    testId: "contact-phone",
  },
  {
    Icon: MapPin,
    label: "Office",
    value: "45/13 Vettukattuvalasu, Vivekanandar Salai, Erode – 638011",
    href: MAP_LINK,
    note: "Tamil Nadu, India",
    testId: "contact-address",
  },
];

const SOCIALS = [
  { Icon: Instagram, label: "Instagram", href: "https://instagram.com" },
  { Icon: Linkedin, label: "LinkedIn", href: "https://linkedin.com" },
  { Icon: Twitter, label: "Twitter", href: "https://twitter.com" },
];

export default function Contact() {
  return (
    <div data-testid="contact-page" className="bg-[#050505] text-[#F3E8E0] min-h-screen">
      {/* HERO */}
      <section className="relative pt-32 pb-16 md:pt-44 md:pb-24 overflow-hidden grain-bg">
        <div className="absolute top-32 right-1/4 w-[500px] h-[500px] bg-[#FF4A00] opacity-[0.08] rounded-full blur-[180px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <SectionLabel className="mb-6">Get in touch</SectionLabel>
            <h1
              data-testid="contact-hero-title"
              className="font-display text-5xl md:text-7xl lg:text-[8rem] font-semibold tracking-tighter leading-[0.92] max-w-5xl"
            >
              Let&rsquo;s talk about your <span className="italic text-[#FF4A00] font-medium">next campaign.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#A3A3A3] max-w-2xl leading-relaxed">
              Whether you&rsquo;re a brand, creator or agency — reach out and our team will get back within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CONTACT METHODS */}
      <section className="pb-16" data-testid="contact-methods">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {CONTACT_METHODS.map((m, i) => (
              <motion.a
                key={i}
                href={m.href}
                target={m.label === "Office" ? "_blank" : undefined}
                rel="noopener noreferrer"
                data-testid={m.testId}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="card-glow group block p-8 md:p-10 border border-[#1a1a1a] rounded-2xl bg-[#0a0a0a] hover:border-[#FF4A00]/40 transition-colors"
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="w-12 h-12 rounded-xl bg-[#FF4A00]/10 flex items-center justify-center group-hover:bg-[#FF4A00]/20 transition-colors">
                    <m.Icon className="w-6 h-6 text-[#FF4A00]" />
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-[#A3A3A3] group-hover:text-[#FF4A00] group-hover:rotate-45 transition-all" />
                </div>
                <div className="label-eyebrow mb-3">{m.label}</div>
                <div className="font-display text-xl md:text-2xl font-medium tracking-tight text-[#F3E8E0] leading-snug break-words">
                  {m.value}
                </div>
                <div className="mt-4 text-sm text-[#A3A3A3]">{m.note}</div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* ADDRESS CARD */}
      <section className="py-16 md:py-24" data-testid="contact-address-card">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="relative overflow-hidden rounded-3xl border border-[#1a1a1a] bg-gradient-to-br from-[#0c0c0c] to-[#070707] p-10 md:p-16">
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#FF4A00] opacity-20 rounded-full blur-[120px] pointer-events-none" />

            {/* Map-style abstract grid */}
            <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
              style={{
                backgroundImage:
                  "linear-gradient(to right, #FF4A00 1px, transparent 1px), linear-gradient(to bottom, #FF4A00 1px, transparent 1px)",
                backgroundSize: "60px 60px",
              }}
            />

            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <SectionLabel className="mb-5">Visit us</SectionLabel>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[0.95]">
                  Incer Technovation Pvt Ltd.
                </h2>
                <div className="mt-8 space-y-1 text-lg text-[#F3E8E0]">
                  <div>45/13 Vettukattuvalasu</div>
                  <div>Vivekanandar Salai</div>
                  <div>Erode – 638011</div>
                  <div className="text-[#A3A3A3]">Tamil Nadu, India</div>
                </div>

                <div className="mt-8 flex gap-3">
                  {SOCIALS.map(({ Icon, label, href }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`contact-social-${label.toLowerCase()}`}
                      aria-label={label}
                      className="w-11 h-11 rounded-full border border-[#222] flex items-center justify-center text-[#A3A3A3] hover:text-[#FF4A00] hover:border-[#FF4A00] transition-colors"
                    >
                      <Icon className="w-5 h-5" />
                    </a>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-5">
                <a
                  href={MAP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-map"
                  className="relative aspect-square rounded-2xl border border-[#222] bg-[#050505] flex items-center justify-center overflow-hidden group hover:border-[#FF4A00] transition-colors"
                >
                  <div className="absolute inset-0 opacity-30"
                    style={{
                      backgroundImage:
                        "linear-gradient(to right, #FF4A00 1px, transparent 1px), linear-gradient(to bottom, #FF4A00 1px, transparent 1px)",
                      backgroundSize: "40px 40px",
                    }}
                  />
                  <div className="relative text-center">
                    <div className="w-16 h-16 rounded-full bg-[#FF4A00] flex items-center justify-center mx-auto mb-4 glow-orange group-hover:scale-105 transition-transform">
                      <MapPin className="w-7 h-7 text-white" />
                    </div>
                    <div className="font-display text-2xl font-medium">Erode, TN</div>
                    <div className="text-sm text-[#A3A3A3] mt-1">11.34° N, 77.70° E</div>
                    <div className="text-sm text-[#FF4A00] mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                      Open in Google Maps
                    </div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
